import React, { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { renderToStaticMarkup } from 'react-dom/server';
import * as BvtIcon from 'bvt-icon';
import 'leaflet.markercluster';
import 'leaflet.markercluster.placementstrategies';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import _ from 'lodash';
import { Space } from 'antd';
import { GA_Utils } from '../../../../../shared/utils/GA-Utils/GA-Utils';
import { Svg } from '@bvt-atoms';
import { getIcon } from '@bvt-shared/utils/iconUtils';

const parseStringHTML = (param) => {
  try {
    return JSON.parse(
      new DOMParser()
        .parseFromString(param, 'text/html')
        .querySelector('.MarkerCluster__marker').dataset.meta
    );
  } catch (error) {
    return {};
  }
};

const StripRenderer = (props) => {
  return (
    <Space align='center'>
      {props.icon ? <Svg src={getIcon('poi', props.icon)} type='link' /> : <BvtIcon.DefaultPoi height='22' width='22' />}
      <div style={{ fontSize: '0.75rem', fontWeight: '600' }}>
        {props.name}
      </div>
      <div style={{ fontSize: '0.75rem' }}>{props.number}</div>
    </Space>
  );
};
export function MarkerClusterWithChildren(props) {
  const map = useMap();
  const activeMarkerRef = useRef(null);
  const [popupPosition, setPopupPosition] = useState({
    lat: null,
    lng: null,
    properties: {},
  });

  const handleClosePopup = () => {
    const tip = document.querySelector('.leaflet-popup-tip-container');
    const closeBtn = document.querySelector('.leaflet-popup-close-button');
    if (tip && closeBtn) {
      tip.style.display = 'none';
      closeBtn.style.display = 'none';
    }
    setPopupPosition({
      lat: null,
      lng: null,
      properties: {},
    });
    activeMarkerRef.current = null;
  };

  useEffect(() => {
    const markerClusterGroupInstance = new L.MarkerClusterGroup({
      animate: false,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      maxClusterRadius: 32,
      elementsPlacementStrategy: 'concentric',
      elementsMultiplier: 1,
      spiderfyDistanceMultiplier: 1.2,
      firstCircleElements: 5,
      animateAddingMarkers: false,
      iconCreateFunction: (icfParam) => {
        const ndta = icfParam
          .getAllChildMarkers()
          .map((vgacm) => parseStringHTML(vgacm.getIcon().options.html));
        const groupedByCategory = _.groupBy(ndta, 'properties.category_name');
        const subClassSum = _.map(groupedByCategory, (group, key) => ({
          category_name: key,
          count: group.length,
          icon: group[0]['icon']
        }));
        const multiplier = map.getZoom() > 16 ? 8 : 3;
        const totalMarkerPerCluster = icfParam.getChildCount();
        const percentageOfTotalMarker =
          (totalMarkerPerCluster / props.data.length) * 100;
        const opacityMultiplier = (percentageOfTotalMarker / 10) * multiplier;
        const sizeMultiPlier = opacityMultiplier > 0.9 ? 1.3 : 0.8;

        const baseStyle = {
          width: 52 * sizeMultiPlier,
          height: 52 * sizeMultiPlier,
          borderRadius: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `rgba(248,129,129,0.1)`,
          opacity: props.opacity,
        };
        const htmlElm = renderToStaticMarkup(
          <div style={baseStyle}>
            <div
              style={{
                ...baseStyle,
                width: 42 * sizeMultiPlier,
                height: 42 * sizeMultiPlier,
                background: `rgba(248,129,129,0.2)`,
              }}
            >
              <div
                style={{
                  ...baseStyle,
                  width: 32 * sizeMultiPlier,
                  height: 32 * sizeMultiPlier,
                  background: `rgba(248,129,129,${opacityMultiplier})`,
                }}
              >
                <div
                  style={{ color: '#fff', fontSize: '1rem', fontWeight: 600 }}
                >
                  {totalMarkerPerCluster}
                </div>
              </div>
            </div>
          </div>
        );

        const htmlToolTipElm = renderToStaticMarkup(
          <Space direction='vertical'>
            <div style={{ fontSize: '0.75rem', fontWeight: '600' }}>
              Classification (Count)
            </div>
            {subClassSum.map((v, index) => {
              const idx = index + 1;
              return (
                <StripRenderer icon={v.icon} key={idx} name={v.category_name} number={v.count} />
              );
            })}
          </Space>
        );
        icfParam.bindTooltip(
          L.tooltip({ content: htmlToolTipElm, direction: 'top', sticky: true })
        );

        return L.divIcon({ html: htmlElm, iconAnchor: { x: 22, y: 22 } });
      },
    });

    markerClusterGroupInstance.setZIndex(101);

    if (props?.data?.length > 0) {
      // Add Cluster To Map
      const start = window.performance.now();

      props.data?.forEach((el) => {

        // Define New instance of marker
        const markerLayer = L.marker(el.position, {
          icon: new L.DivIcon({
            html: renderToStaticMarkup(
              <div
                className='MarkerCluster__marker'
                data-meta={JSON.stringify(el)}
                style={{ opacity: isNaN(props.opacity) ? 1 : props.opacity }}
              >
                {el.icon ? <img
                  alt=''
                  src={getIcon('poi', el.icon)}
                  style={{ height: '2.25rem', width: '2.25rem' }}
                /> : <BvtIcon.DefaultPoi height='22' width='22' />}
              </div>
            ),
            iconSize: [25, 41],
            iconAnchor: [18, 35],
            tooltipAnchor: [0, -32],
            shadowSize: [41, 41],
          }),
        });

        markerLayer.on('click', (e) => {
          const tip = document.querySelector('.leaflet-popup-tip-container');
          const closeBtn = document.querySelector('.leaflet-popup-close-button');
          if (tip && closeBtn) {
            tip.style.display = 'flex';
            closeBtn.style.display = 'flex';
          }
          activeMarkerRef.current = markerLayer;
          setPopupPosition({
            lat: e.latlng.lat,
            lng: e.latlng.lng,
            properties: {
              ...el.properties,
            },
          });
        });

        // Add Marker To cluster Layer
        markerClusterGroupInstance.addLayer(markerLayer);
      });

      markerClusterGroupInstance.addTo(map);
      const end = window.performance.now();

      GA_Utils.trackEvent({
        type: 'DEV',
        eventName: 'CLUSTER_PERFORMANCE',
        properties: {
          total_pois: props?.data?.length,
          timing: end - start,
        },
      });
    }

    map.on('zoomend', () => {
      const activeMarker = activeMarkerRef.current;
      if (activeMarker && !map.hasLayer(activeMarker)) {
        handleClosePopup();
      }
    });

    return () => {
      // Remove Cluster On Remount
      markerClusterGroupInstance.removeFrom(map);
    };
  }, [props.data, props.opacity]);

  const childrenWithProps = React.Children.map(props.children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        lat: popupPosition.lat,
        lng: popupPosition.lng,
        onClose: handleClosePopup,
        properties: popupPosition.properties,
      });
    }
    return child;
  });

  return childrenWithProps;
}
